<template>
  <div class="flex h-screen bg-gray-50">
    <SideBar class="flex-shrink-0" />
    <div class="flex-1 overflow-auto p-4 md:p-6">
      <header class="mb-8">
        <h1 class="text-3xl font-bold text-gray-900">Call Analytics Dashboard</h1>
        <p class="text-gray-600">Track your call performance and talk time</p>
      </header>

      <!-- Filters Section -->
      <div class="bg-white rounded-xl shadow-sm p-6 mb-6 border border-gray-100">
        <div class="flex flex-col gap-4">
          <!-- User Filter -->
          <div class="w-full">
            <label class="block text-sm font-medium text-gray-700 mb-1">Select User</label>
            <div class="flex flex-wrap gap-2" v-if="Object.entries($store.getters['leads/usersFiltered']).length" >
              <button 
                @click="setSelectedUser('all')"
                :class="[
                  'px-4 py-2 rounded-lg font-medium transition-all duration-300 transform hover:scale-105',
                  selectedUser === 'all' 
                    ? 'bg-primary text-black shadow-md' 
                    : 'bg-gray-100 text-gray-700 border border-gray-200'
                ]"
              >
                All Users
              </button>
              <button 
                v-for="name, id in $store.getters['leads/usersFiltered']" 
                :key="id" 
                @click="setSelectedUser(id)"
                :class="[
                  'px-4 py-2 rounded-lg font-medium transition-all duration-300 transform hover:scale-105',
                  selectedUser === id
                    ? 'bg-primary text-black shadow-md' 
                    : 'bg-gray-100 text-gray-700 border border-gray-200'
                ]"
              >
                {{ name }}
              </button>
            </div>
          </div>

          <!-- Time Range Filter -->
          <div class="w-full">
            <label class="block text-sm font-medium text-gray-700 mb-1">Time Range</label>
            <div class="flex flex-wrap gap-2">
              <button 
                v-for="range in timeRanges" 
                :key="range.value" 
                @click="setTimeRange(range.value)"
                :class="[
                  'px-4 py-2 rounded-lg font-medium transition-all duration-300 transform hover:scale-105',
                  selectedRange === range.value 
                    ? 'bg-primary text-black shadow-md' 
                    : 'bg-gray-100 text-gray-700 border border-gray-200'
                ]"
              >
                {{ range.label }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Stats Cards -->
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div 
          v-for="(stat, index) in filteredStats" 
          :key="index"
          class="bg-white rounded-xl shadow-sm p-6 border border-gray-100 transition-all duration-500 hover:shadow-md"
          :class="{'animate-in': isAnimating}"
          :style="{animationDelay: `${index * 100}ms`}"
        >
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm font-medium text-gray-500">{{ stat.label }}</p>
              <p class="text-2xl font-bold text-gray-900">{{ stat.value }}</p>
            </div>
            <div class="p-3 rounded-full bg-primary/10 text-primary">
              <component :is="stat.icon" class="w-6 h-6" />
            </div>
          </div>
          <div class="mt-4 flex items-center text-sm">
            <span :class="stat.trend > 0 ? 'text-green-500' : 'text-red-500'">
              {{ stat.trend > 0 ? '+' : '' }}{{ stat.trend }}%
            </span>
            <span class="text-gray-500 ml-2">vs previous {{ selectedRange }}</span>
          </div>
        </div>
      </div>

      <!-- Charts -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div 
          class="bg-white rounded-xl shadow-sm p-6 border border-gray-100 transition-all duration-500 hover:shadow-md"
          :class="{'animate-in': isAnimating}"
        >
          <div class="flex justify-between items-center mb-4">
            <h2 class="text-xl font-semibold text-gray-900">Call Volume</h2>
          </div>
          <div class="h-80">
            <canvas ref="callsChart"></canvas>
          </div>
        </div>
        
        <div 
          class="bg-white rounded-xl shadow-sm p-6 border border-gray-100 transition-all duration-500 hover:shadow-md"
          :class="{'animate-in': isAnimating}"
          style="animation-delay: 200ms"
        >
          <div class="flex justify-between items-center mb-4">
            <h2 class="text-xl font-semibold text-gray-900">Talk Time (minutes)</h2>
      
          </div>
          <div class="h-80">
            <canvas ref="timeChart"></canvas>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, watch } from 'vue';
import Chart from 'chart.js/auto';
import { Phone, Clock, TrendingUp, Users } from 'lucide-vue-next';
import SideBar from '@/components/SideBar.vue';
import { useStore } from 'vuex'
import dayjs from 'dayjs'

const store = useStore()

// References for chart canvases
const callsChart = ref(null);
const timeChart = ref(null);
let callsChartInstance = null;
let timeChartInstance = null;

// Animation state
const isAnimating = ref(false);

// Time range options
const timeRanges = [
  { label: 'Today', value: 'today' },
  { label: 'This Week', value: 'week' },
  { label: 'This Month', value: 'month' },
  { label: 'Quarter', value: 'quarter' },
  { label: 'Year', value: 'year' },
];

const selectedRange = ref('week');
const selectedUser = ref('all');


// Colors for each user
const userColors = [
  '#3b82f6', // blue
  '#10b981', // green
  '#f59e0b', // amber
  '#ef4444', // red
  '#8b5cf6'  // purple (for all users)
];

// Get current data based on selected time range and user
const getCurrentData = () => {
  const analytics = store.getters['leads/callAnalytics'];
  if (!analytics.data) return null;

  let userData = analytics.data[selectedUser.value];
  if (!userData) {
    // fallback to 'all'
    userData = analytics.data['all'];
  }

  return userData || null;
};



// Computed stats based on selected time range and user

const filteredStats = computed(() => {
  const currentData = getCurrentData();
  if (!currentData) return [];

  const totalCalls = currentData.calls.reduce((sum, val) => sum + val, 0);
  const totalMinutes = currentData.minutes.reduce((sum, val) => sum + val, 0);
  const avgCallLength = totalCalls > 0 ? Math.round(totalMinutes / totalCalls) : 0;

  const analytics = store.getters['leads/callAnalytics'];
  const start = dayjs(analytics.start);
  const end = dayjs(analytics.end);

  // 🛠 count ONLY weekdays
  let businessDays = 0;
  let temp = start.clone();
  while (temp.isBefore(end) || temp.isSame(end)) {
    const day = temp.day();
    if (day !== 0 && day !== 6) {  // 0 = Sunday, 6 = Saturday
      businessDays++;
    }
    temp = temp.add(1, 'day');
  }

  const avgCallsPerDay = totalCalls > 0 && businessDays > 0
    ? Math.round(totalCalls / businessDays)
    : 0;

  return [
    { label: 'Total Calls', value: totalCalls, icon: Phone, trend: 12 },
    { label: 'Talk Time', value: `${totalMinutes} min`, icon: Clock, trend: 8 },
    { label: 'Avg. Call Length', value: `${avgCallLength} min`, icon: TrendingUp, trend: -3 },
    { label: 'Avg. Calls/Business Day', value: avgCallsPerDay, icon: Users, trend: 5 }
  ];
});



// Function to set time range and trigger animation
const setTimeRange = async (range) => {
  selectedRange.value = range;
  await store.dispatch('leads/fetchCallAnalytics', range); // ✅ Refetch API
  triggerAnimation();
  updateCharts();

};

// Function to set selected user and trigger animation
const setSelectedUser = (userId) => {
  selectedUser.value = userId;
  triggerAnimation();
};

// Function to trigger animation
const triggerAnimation = () => {
  isAnimating.value = false;
  setTimeout(() => {
    isAnimating.value = true;
  }, 50);
};

// Function to create/update charts with multiple lines
const updateCharts = () => {
  const analytics = store.getters['leads/callAnalytics'];
  if (!analytics.data || !analytics.data.all) return;  // if no data yet, skip

  const timeRangeData = analytics.data;
  const labels = analytics.data['all']?.labels || [];

  const callsDatasets = [];
  const timeDatasets = [];

  if (selectedUser.value === 'all' && store.getters['leads/usersFiltered']) {
    // Show all users
    Object.entries(store.getters['leads/usersFiltered']).forEach(([id, name], index) => {
      const userData = timeRangeData[id];
      if (!userData) return;
      
      callsDatasets.push({
        label: name,
        data: userData.calls,
        backgroundColor: `${userColors[index]}20`,
        borderColor: userColors[index],
        borderWidth: 2,
        tension: 0.4,
        fill: false,
        pointBackgroundColor: userColors[index],
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        pointRadius: 4,
        pointHoverRadius: 6
      });

      timeDatasets.push({
        label: name,
        data: userData.minutes,
        backgroundColor: `${userColors[index]}20`,
        borderColor: userColors[index],
        borderWidth: 2,
        tension: 0.4,
        fill: false,
        pointBackgroundColor: userColors[index],
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        pointRadius: 4,
        pointHoverRadius: 6
      });
    });
  } else {
    // Show selected user only
    const userData = timeRangeData[selectedUser.value];
    if (!userData) return;

    const userIndex = Object.entries(store.getters['leads/usersFiltered']).findIndex(([id]) => id == selectedUser.value);
    const userName = store.getters['leads/usersFiltered'][selectedUser.value] || 'Unknown';

    callsDatasets.push({
      label: userName,
      data: userData.calls,
      backgroundColor: `${userColors[userIndex]}20`,
      borderColor: userColors[userIndex],
      borderWidth: 3,
      tension: 0.4,
      fill: true,
      pointBackgroundColor: userColors[userIndex],
      pointBorderColor: '#fff',
      pointBorderWidth: 2,
      pointRadius: 5,
      pointHoverRadius: 8
    });

    timeDatasets.push({
      label: userName,
      data: userData.minutes,
      backgroundColor: `${userColors[userIndex]}20`,
      borderColor: userColors[userIndex],
      borderWidth: 3,
      tension: 0.4,
      fill: true,
      pointBackgroundColor: userColors[userIndex],
      pointBorderColor: '#fff',
      pointBorderWidth: 2,
      pointRadius: 5,
      pointHoverRadius: 8
    });
  }

  // Destroy old charts
  if (callsChartInstance) callsChartInstance.destroy();
  if (timeChartInstance) timeChartInstance.destroy();

  // Rebuild charts
  const createChartConfig = (datasets) => ({
  type: 'line',
  data: {
    labels,
    datasets
  },
  options: {
    responsive: true,
    maintainAspectRatio: false,
    spanGaps: true, // ✅ Connect gaps
    animation: {
      duration: 1000,
      easing: 'easeOutQuart'
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(200, 200, 200, 0.1)'
        }
      },
      x: {
        grid: {
          display: false
        }
      }
    },
    plugins: {
      legend: {
        display: true,
        position: 'top',
        labels: {
          boxWidth: 12,
          usePointStyle: true,
          pointStyle: 'circle',
          font: {
            size: 10
          }
        }
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        padding: 10,
        cornerRadius: 6,
        titleFont: {
          size: 14,
          weight: 'bold'
        }
      }
    },
    interaction: {
      mode: 'index',
      intersect: false
    },
    elements: {
      point: {
        radius: 3,            // 🛠 Small points, so single points still render
        hitRadius: 10,
        hoverRadius: 8
      },
      line: {
        tension: 0.4,         // 🛠 Smooth curves
        borderWidth: 3
      }
    }
  }
});


  callsChartInstance = new Chart(callsChart.value, createChartConfig(callsDatasets));
  timeChartInstance = new Chart(timeChart.value, createChartConfig(timeDatasets));
};


// Watch for changes in selected range or user
watch([selectedRange, selectedUser], () => {
  updateCharts();
});

// Initialize on component mount
onMounted(async () => {
  await store.dispatch('leads/fetchUserMap');
  await store.dispatch('leads/fetchCallAnalytics', selectedRange.value); // ✅ Fetch analytics for selected range
  updateCharts();
  triggerAnimation();
});
</script>

<style>
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-in {
  animation: fadeInUp 0.6s ease-out forwards;
  opacity: 0;
}

:root {
  --primary: #3b82f6;
  --primary-dark: #2563eb;
}

.bg-primary {
  background-color: var(--primary);
}

.text-primary {
  color: var(--primary);
}
</style>